import { EventType } from "components/components/Remit/RemitFiltersForm/remitFilters";
import RemitInfoPopup from "components/homepage/Remit/RemitInfoPopup/RemitInfoPopup";
import { ItemName } from "components/homepage/style";
import PercentageDonutChart from "components/shared/PercentageDonutChart/PercentageDonutChart";
import { EventStatus } from "models/remit/remitMessageDetailsModel";
import React, { useState } from "react";
import { DateFormat, formatDateTimeString } from "utils/dateHelpers";
import { formatCapacity } from "utils/remitHelpers";

import {
  ChartContainer,
  DoughnutOutline,
  InterconnectorInformation,
  RemitMiniTilePanel,
  UnavailableCapacity,
  LabelValueRow,
  ValueText,
  TileButton,
  RemitListItem,
} from "./style";

interface Props {
  mrid: string;
  messageId: number;
  assetId: string;
  eventStatus: EventStatus;
  normalCapacity: number;
  availableCapacity: number;
  unavailableCapacity: number;
  fuelType: string | null;
  eventStartTime: Date;
  eventEndTime: Date | null;
  publishTime: Date;
  unavailabilityType: string;
  eventType: string;
}

const isInterconnector = (assetId: string): boolean => assetId.startsWith("I");

const RemitItem: React.FC<Props> = ({
  mrid,
  messageId,
  assetId,
  eventStatus,
  normalCapacity,
  availableCapacity,
  unavailableCapacity,
  fuelType,
  eventStartTime,
  eventEndTime,
  publishTime,
  unavailabilityType,
  eventType,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const percentageAvailable =
    availableCapacity === 0
      ? 0
      : (availableCapacity * 100) / (availableCapacity + unavailableCapacity);

  const type =
    eventType === EventType.SpaceWeatherEvent
      ? "Space Weather Event"
      : unavailabilityType;

  const ItemContent = (
    <RemitMiniTilePanel isMiniTile>
      <TileButton
        onClick={(): void => setShowModal(true)}
        onMouseEnter={(): void => setIsHovered(true)}
        onMouseLeave={(): void => setIsHovered(false)}
      >
        <div>
          <ItemName>{assetId}</ItemName>
          <LabelValueRow>
            <ValueText>Event start:</ValueText>
            <ValueText>
              {formatDateTimeString(eventStartTime, DateFormat.DateTime)}
            </ValueText>
          </LabelValueRow>
          {eventEndTime && (
            <LabelValueRow>
              <ValueText>Event end:</ValueText>
              <ValueText>
                {formatDateTimeString(eventEndTime, DateFormat.DateTime)}
              </ValueText>
            </LabelValueRow>
          )}
          <LabelValueRow>
            <ValueText>Published:</ValueText>
            <ValueText>
              {formatDateTimeString(publishTime, DateFormat.DateTime)}
            </ValueText>
          </LabelValueRow>
          <LabelValueRow>
            <ValueText>Type:</ValueText>
            <ValueText>{type}</ValueText>
          </LabelValueRow>
        </div>
        {isInterconnector(assetId) ? (
          <InterconnectorInformation>
            <div>
              <ValueText>Unavailable capacity:</ValueText>
            </div>
            <UnavailableCapacity>
              {formatCapacity(unavailableCapacity)}
            </UnavailableCapacity>
          </InterconnectorInformation>
        ) : (
          <ChartContainer>
            <PercentageDonutChart
              percentage={percentageAvailable}
              displayText={formatCapacity(availableCapacity)}
              displayTextLabel="Available"
            />
            <DoughnutOutline
              $isVisible={isHovered}
              aria-hidden
              data-test-id="remit-hover-doughnut"
            />
          </ChartContainer>
        )}
      </TileButton>
    </RemitMiniTilePanel>
  );

  return (
    <RemitListItem data-test-id="remit-item">
      {ItemContent}
      <RemitInfoPopup
        showModal={showModal}
        setShowModal={setShowModal}
        mrid={mrid}
        messageId={messageId}
        assetId={assetId}
        eventStatus={eventStatus}
        eventStartTime={eventStartTime}
        eventEndTime={eventEndTime}
        fuelType={fuelType}
        normalCapacity={normalCapacity}
        availableCapacity={availableCapacity}
        unavailableCapacity={unavailableCapacity}
        unavailabilityType={unavailabilityType}
        eventType={eventType}
      />
    </RemitListItem>
  );
};

export default RemitItem;
